import { Alert, Box, Button, FormControlLabel, InputAdornment, Radio, Stack, Switch, TextField, Typography } from "@mui/material";
import CustomPopover, { usePopover } from "../custom-popover";
import useDatePreset, { presetValues } from "src/hooks/use-date-preset";
import { useEffect, useState } from "react";
import DateRagePicker from "../custom-date-range-picker/custom-date-range-picker";
import { useDispatch, useSelector } from "react-redux";
import { useLocales } from "src/locales";
import { useResponsive } from "src/hooks/use-responsive";
import { differenceInDays, format } from 'date-fns';
import { setDatesForcedByActivation, setFilterCompare, setFilterDailyDisabled, setFilterTimestep, setSearchPerfDateOverridden } from 'src/context/reducers/filter-settings/filterSlice';
import Iconify from "../iconify";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import { getSearchPerformanceDateRange, isSearchPerformancePath, rememberSearchPerformanceDateRange } from "src/utils/searchPerformanceDateMemory";
import { isTimestepAllowed, resolveTimestep } from "src/utils/visibility-timestep";

interface DatePreset {
    displayName: string;
    start: Date;
    end: Date;
    step: string;
}

interface Props {
    features?: { time_step: boolean };
    startDate: Date;
    compareStartDate: any;
    compareEndDate: any;

    endDate: Date;
    timeStep?: any;
    selected: string;
    preset: string;
    onChangeStartDate: (date: Date | null) => void;
    onChangeEndDate: (date: Date | null) => void;
    // Optional atomic range setter. When provided, `apply()` emits both bounds in a single
    // call instead of two separate onChangeStart/onChangeEnd calls. This avoids a stale-closure
    // double-dispatch in consumers that rebuild the opposite bound from the store (cf. header).
    onChangeRange?: (start: Date | null, end: Date | null) => void;

    onChangeCompareStartDate: (date: Date | null) => void;
    onChangeCompareEndDate: (date: Date | null) => void;

    onChangePreset: (value: string) => void;
    onChangeTimeStep?: (value: any) => void;
}

interface Options {
    step: string;
    preset: string;
    selected?: "custom" | "preset";
    start?: Date;
    end?: Date;
}

// --- Laisse bien ce hook pour rétrocompatibilité (anciennes pages) ---
export const useDateRangeSelect = (options: Options) => {
    const datePreset = useDatePreset(options.preset);
    const [startDate, setStartDate] = useState<Date | null>(options.start || null);
    const [endDate, setEndDate] = useState<Date | null>(options.end || null);
    const [selected, setSelected] = useState<"custom" | "preset">(options.selected || "preset");
    const [timeStep, setTimeStep] = useState<any>(options.step || presetValues[options.preset].step);

    const onChangePreset = (value: string) => {
        datePreset.setPreset(value);
        setTimeStep(presetValues[value].step);
        setSelected("preset");
    };

    const onChangeStartDate = (date: Date | null) => {
        setStartDate(date);
        setSelected("custom");
    };

    const onChangeEndDate = (date: Date | null) => {
        setEndDate(date);
        setSelected("custom");
    };

    const onChangeTimeStep = (value: any) => {
        setTimeStep(value);
    };

    return {
        preset: selected === "preset" ? datePreset : null,
        start: startDate,
        end: endDate,
        step: timeStep,
        selected,
        onChangeEndDate,
        onChangeStartDate,
        onChangePreset,
        onChangeTimeStep,
    };
};

// --- Ton nouveau composant principal ---
export default function DateRangeSelect(props: Props) {
    const { t } = useLocales();
    const calendar = usePopover();
    const dispatch = useDispatch();
    const [preset, setPreset] = useState<string>(props.preset);
    const [startDate, setStartDate] = useState<Date | null>(props.startDate);
    const filterStore = useSelector((state: any) => state.filters);
    const workspace = useCurrentWorkspace();
    const [compareStartDate, setCompareStartDate] = useState<Date | null>(props.compareStartDate);
    const [compareEndDate, setCompareEndDate] = useState<Date | null>(props.compareEndDate);

    const [endDate, setEndDate] = useState<Date | null>(props.endDate);
    const [timeStep, setTimeStep] = useState<any | null>(null);
    const [selected, setSelected] = useState<"custom" | "preset">("preset");

    const [enableComparison, setEnableComparison] = useState(filterStore?.compare || false);
    const [userSelectedTimeStep, setUserSelectedTimeStep] = useState<any | null>(null); // Ajout
    const lgUp = useResponsive('up', 'lg');
    const startDateStore = useSelector((state: any) => state.filters.start);
    const endDateStore = useSelector((state: any) => state.filters.end);
    // Visibility has no Daily: its scraping tables are weekly.
    const dailyAvailable = isTimestepAllowed('date_day', location?.pathname);

    // A granularity picked on another page (Daily on Search Performance) may not
    // exist here: fall back to auto, which also lets it come back once the user
    // leaves the page.
    useEffect(() => {
        if (userSelectedTimeStep && !isTimestepAllowed(userSelectedTimeStep, location?.pathname)) {
            setUserSelectedTimeStep(null);
        }
    }, [userSelectedTimeStep, location?.pathname]);

    useEffect(() => {
        const isDemo = workspace?.name?.toLowerCase().includes("demo");
        const currentPath = location?.pathname;

        if (isDemo && currentPath === "/brand/search-performance/overview") return;

        const rememberedRange = isSearchPerformancePath(window.location.pathname)
            ? getSearchPerformanceDateRange(workspace?.id)
            : null;

        // Bing seul n'a pas de Daily : l'effet Bing plus bas force year_week. Sans ce garde-fou,
        // les deux effets se renvoient date_day / year_week à l'infini (Maximum update depth).
        const bingOnly = filterStore?.platforms?.length === 1 && filterStore?.platforms.includes('Bing');
        const effectiveUserTimeStep = bingOnly && userSelectedTimeStep === 'date_day' ? 'year_week' : userSelectedTimeStep;

        if (!userSelectedTimeStep && startDateStore && endDateStore) {
            const days = differenceInDays(new Date(endDateStore), new Date(startDateStore));
            let nextTimeStep: any = "date_day";

            if (days <= 14) nextTimeStep = "date_day";
            else if (days <= 90) nextTimeStep = "year_week";
            else nextTimeStep = "year_month";
            nextTimeStep = resolveTimestep(nextTimeStep, currentPath);
            if (bingOnly && nextTimeStep === 'date_day') nextTimeStep = 'year_week';

            if (timeStep !== nextTimeStep) {
                setTimeStep(nextTimeStep);
            }
            if (filterStore?.timestep !== nextTimeStep) {
                dispatch(setFilterTimestep({
                    timestep: nextTimeStep,
                    skipAlignment: selected === 'custom' || Boolean(rememberedRange),
                }));
            }
        } else if (effectiveUserTimeStep && timeStep !== effectiveUserTimeStep) {
            setTimeStep(effectiveUserTimeStep);
        }
    }, [
        dispatch,
        endDateStore,
        startDateStore,
        filterStore?.platforms,
        userSelectedTimeStep,
        workspace?.id,
        workspace?.name,
        location?.pathname,
        selected,
        timeStep,
        filterStore?.timestep,
    ]);

    useEffect(() => {
        if (!calendar.open) {
            const rememberedRange = isSearchPerformancePath(window.location.pathname)
                ? getSearchPerformanceDateRange(workspace?.id)
                : null;

            setEnableComparison(filterStore?.compare || false);
            setStartDate(props.startDate);
            setEndDate(props.endDate);
            setCompareStartDate(props.compareStartDate);
            setCompareEndDate(props.compareEndDate);

            if (rememberedRange) {
                setSelected('custom');
            }
        }
    }, [
        calendar.open,
        filterStore?.compare,
        props.startDate,
        props.endDate,
        props.compareStartDate,
        props.compareEndDate,
        workspace?.id,
        location?.pathname,
    ]);

    const onChangeStartDate = (date: Date | null) => {
        setStartDate(date);
        setSelected("custom");
    }

    const onChangeCompareStartDate = (date: Date | null) => {
        setCompareStartDate(date);
        setSelected("custom");
    }

    const onChangeCompareEndDate = (date: Date | null) => {
        setCompareEndDate(date);
        setSelected("custom");
    }

    const onChangeEndDate = (date: Date | null) => {
        setEndDate(date);
        setSelected("custom");
    }

    const handleEnableComparisonChange = () => {
        setEnableComparison(!enableComparison);
    };

    const handleTimeStepChange = (value: any) => {
        setUserSelectedTimeStep(value);
        setTimeStep(value);
    };

    const apply = () => {
        // Calculer le nombre de jours dans la période de comparaison et de sélection
        const selectedDays = startDate && endDate ? differenceInDays(endDate, startDate) : 0;
        const compareDays = compareStartDate && compareEndDate ? differenceInDays(compareEndDate, compareStartDate) : 0;
        const appliedStartDate = selected === 'preset'
            ? presetValues[preset].start
            : startDate;
        const appliedEndDate = selected === 'preset'
            ? presetValues[preset].end
            : endDate;

        if (
            isSearchPerformancePath(window.location.pathname) &&
            appliedStartDate &&
            appliedEndDate
        ) {
            rememberSearchPerformanceDateRange({
                workspaceId: workspace?.id ?? null,
                start: format(appliedStartDate, 'yyyy-MM-dd'),
                end: format(appliedEndDate, 'yyyy-MM-dd'),
                compareStart: compareStartDate ? format(compareStartDate, 'yyyy-MM-dd') : null,
                compareEnd: compareEndDate ? format(compareEndDate, 'yyyy-MM-dd') : null,
                compare: enableComparison,
            });
            dispatch(setSearchPerfDateOverridden(true));
        }

        dispatch(setDatesForcedByActivation(false)); // 🧠 on bloque les autres effets

        // Check if the periods match
        // if (enableComparison && selectedDays !== compareDays) {
        //     enqueueSnackbar('The selected and comparison \n date ranges do not match \n in the number of days.', { variant: 'warning' });
        // }

        if (selected === 'preset') {
            const { start, end } = presetValues[preset];
            if (props.onChangeRange) {
                props.onChangeRange(start, end);
            } else {
                props.onChangeStartDate(start);
                props.onChangeEndDate(end);
            }
        } else if (props.onChangeRange) {
            props.onChangeRange(startDate, endDate);

            props.onChangeCompareStartDate(compareStartDate);
            props.onChangeCompareEndDate(compareEndDate);
        } else {
            props.onChangeEndDate(endDate);
            props.onChangeStartDate(startDate);

            props.onChangeCompareStartDate(compareStartDate);
            props.onChangeCompareEndDate(compareEndDate);
        }

        if (props.features?.time_step === true && timeStep) {
            dispatch(setFilterTimestep({
                timestep: resolveTimestep(timeStep, window.location.pathname),
                skipAlignment:
                    selected === 'custom' ||
                    isSearchPerformancePath(window.location.pathname),
            }));
        }

        dispatch(setFilterCompare(enableComparison));

        calendar.onClose();
    };

    const selectedDays = startDate && endDate ? differenceInDays(endDate, startDate) : 0;
    const compareDays = compareStartDate && compareEndDate ? differenceInDays(compareEndDate, compareStartDate) : 0;

    useEffect(() => {
        const bing = filterStore?.platforms.length === 1 && filterStore?.platforms.includes('Bing')
        const multiple = filterStore?.platforms.length > 1 && filterStore?.platforms.includes('Bing')
        const all = filterStore?.platforms.length === 0

        if (filterStore.timestep === 'date_day' && bing) {
            const rememberedRange = isSearchPerformancePath(window.location.pathname)
                ? getSearchPerformanceDateRange(workspace?.id)
                : null;

            if (timeStep !== 'year_week') {
                setTimeStep('year_week')
            }
            dispatch(setFilterTimestep({
                timestep: 'year_week',
                skipAlignment: selected === 'custom' || Boolean(rememberedRange),
            }))
            if (!filterStore?.dailyDisabled) {
                dispatch(setFilterDailyDisabled(true))
            }
        } else if (filterStore.timestep !== 'date_day' && bing) {
            if (!filterStore?.dailyDisabled) {
                dispatch(setFilterDailyDisabled(true))
            }
        }
        else if ((all || multiple) && window.location.pathname != "/brand-overview") {
            if (filterStore?.dailyDisabled) {
                dispatch(setFilterDailyDisabled(false))
            }
        }
    }, [
        dispatch,
        filterStore?.platforms,
        filterStore?.timestep,
        filterStore?.dailyDisabled,
        selected,
        timeStep,
        workspace?.id,
    ])

    return (
        <>
            <TextField
                size="small"
                disabled={
                    location?.pathname === "/brand-overview" ||
                    location?.pathname.startsWith("/competitors/conquest-mode")
                }
                onClick={calendar.onOpen}
                sx={{
                    borderRadius: 1,
                    width: { sm: "100%", xs: "100%", lg: location?.pathname.startsWith("/competitors/conquest-mode") ? 140 : 233 },
                    backgroundColor: "transparent",
                    "& input": {
                        color: "white",
                    },
                }}
                value={location?.pathname.startsWith("/competitors/conquest-mode") ? "Last 30 days" : props.selected}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <Iconify icon={"solar:calendar-line-duotone"} color={'rgba(145, 158, 171, 1)'} width={20} sx={{ position: 'relative', top: -1 }} />
                        </InputAdornment>
                    ),
                }}
            />
            <CustomPopover
                sx={{ pt: 2, px: 1, pb: 0, display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}
                open={calendar.open}
                onClose={calendar.onClose}
            >

                <Stack sx={{ width: '100%', position: 'relative' }} display={'flex'} flexDirection={'column'}>
                    <DateRagePicker
                        onClose={calendar.onClose}
                        variant="calendar"
                        startDate={selected === "custom" ? startDate : presetValues[preset].start}
                        endDate={selected === "custom" ? endDate : presetValues[preset].end}
                        onChangeEndDate={onChangeEndDate}
                        onChangeStartDate={onChangeStartDate}
                        onChangeCompareStartDate={onChangeCompareStartDate}
                        enableComparison={enableComparison}
                        onChangeCompareEndDate={onChangeCompareEndDate}
                    />

                    <Box
                        sx={{
                            ml: 1,
                            mt: 2,
                            position: lgUp ? 'absolute' : 'relative',
                            top: lgUp ? (enableComparison && selectedDays !== null && compareDays !== null && selectedDays !== compareDays && compareDays !== 29 ? 330 : 320) : 0,
                            left: lgUp ? 17 : 0
                        }}
                    >
                        <FormControlLabel
                            control={
                                <Switch
                                    size={lgUp ? "small" : "medium"}
                                    checked={enableComparison}
                                    onChange={handleEnableComparisonChange}
                                />
                            }
                            label={<Box sx={{ fontSize: lgUp ? 12 : 14 }}>{t('dashboard.filters.enable_comparison')}</Box>}
                        />

                    </Box>

                    <Stack flexDirection={'row'} height={'100%'} spacing={2} mb={2} mt={2} ml={1}>
                        {props.features?.time_step === true ? (
                            <Box display={location?.pathname === "/dash/brand-wayback" ? "none" : "block"}>
                                <Typography fontSize={15}>{t('dashboard.filters.group_data')}: </Typography>
                                {/* Les boutons radios (pas de Daily sur Visibility), rien de sélectionné si auto */}
                                {dailyAvailable && (
                                    <FormControlLabel
                                        control={
                                            <Radio
                                                checked={userSelectedTimeStep === 'date_day'}
                                                onChange={() => handleTimeStepChange('date_day')}
                                                disabled={filterStore.dailyDisabled}
                                            />
                                        }
                                        label={t('dashboard.filters.daily')}
                                    />
                                )}
                                <FormControlLabel
                                    control={
                                        <Radio
                                            checked={userSelectedTimeStep === 'year_week'}
                                            onChange={() => handleTimeStepChange('year_week')}
                                        />
                                    }
                                    label={t('dashboard.filters.weekly')}
                                />
                                <FormControlLabel
                                    control={
                                        <Radio
                                            checked={userSelectedTimeStep === 'year_month'}
                                            onChange={() => handleTimeStepChange('year_month')}
                                        />
                                    }
                                    label={t('dashboard.filters.monthly')}
                                />
                            </Box>
                        ) : (
                            ''
                        )}
                        <Box flexGrow={1} />
                        <Stack alignSelf={'end'} direction={'row'} spacing={2}>
                            <Button
                                sx={{ maxHeight: '30px', my: 'auto' }}
                                variant="outlined"
                                color="inherit"
                                onClick={() => {
                                    calendar.onClose();
                                    setEnableComparison(filterStore?.compare || false);
                                }}
                            >
                                {t('dashboard.global.cancel')}
                            </Button>
                            <Button
                                sx={{ maxHeight: '30px', my: 'auto' }}
                                color="primary"
                                variant="contained"
                                onClick={apply}
                            >
                                {t('dashboard.filters.ok')}
                            </Button>
                        </Stack>
                    </Stack>
                </Stack>
            </CustomPopover>
        </>
    )
}